<template>
  <div class="image-exams-section px-3" v-show="!loading && exams.length > 0">
    <v-card outlined class="box">
      <div class="pa-4">
        <v-row>
          <v-col>
            <p class="ma-0">
              <v-icon color="primary">mdi-seat-flat-angled</v-icon>
              <span class="ml-2 font-weight-bold text-uppercase primary--text">
                Últimos Exames de Imagens
              </span>
            </p>
          </v-col>
          <v-col class="text-right">
            <v-btn small outlined color="primary" :to="'/exams?tab=images'">
              Lista completa
            </v-btn>
          </v-col>
        </v-row>
      </div>
      <v-divider />
      <LoadingContainer v-if="loading" />
      <p v-else-if="exams.length == 0">Nenhum exame encontrado.</p>
      <div v-else class="exams">
        <div v-for="(exam, index) in exams" :key="index" class="px-4 py-2">
          <div class="pa-4">
            <v-row no-gutters class="link-color">
              <v-col cols="12" sm="2">
                <p class="font-weight-medium">
                  <v-icon color="primary">mdi-calendar</v-icon>
                  {{ exam.date | njDate }}
                </p>
              </v-col>
              <v-col cols="12" sm="10">
                <p>
                  <v-icon color="primary" class="mr-1">mdi-account</v-icon>
                  Solicitante: <strong>{{ exam.doctor_requestor }}</strong>
                </p>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <p class="font-weight-medium">
                  {{ exam.name }}
                </p>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col
                class="text-left text-sm-right d-flex flex-wrap justify-start justify-sm-end"
              >
                <div
                  class="ml-2"
                  :style="{ order: buttonOrder('laudo') }"
                  v-if="isButtonEnabled('laudo')"
                >
                  <ReportDialog :lau-isn="exam.lau_isn" :pad-isn="exam.id">
                    <template v-slot:open="{ open }">
                      <v-btn
                        @click="open"
                        text
                        class="text-capitalize font-weight-regular"
                      >
                        <v-icon left color="primary">mdi-clipboard-text</v-icon>
                        <span class="link-color">{{ buttonLabel('laudo', 'Ver Laudo') }}</span>
                      </v-btn>
                    </template>
                  </ReportDialog>
                </div>
                <div
                  class="ml-2"
                  :style="{ order: buttonOrder('anexo') }"
                  v-if="showPdfImages && isButtonEnabled('anexo')"
                >
                  <PacsButton :has-images="exam.origin.imagem_pacs">
                    <template v-slot:default="{ open, loading }">
                      <v-btn
                        text
                        :disabled="loading"
                        :loading="loading"
                        class="link-color text-capitalize font-weight-regular"
                        @click.prevent="open(exam.origin, true)"
                      >
                        <v-icon left color="primary">mdi-file-download</v-icon>
                        <span class="link-color">{{ buttonLabel('anexo', 'Imagens (PDF)') }}</span>
                      </v-btn>
                    </template>
                  </PacsButton>
                </div>
                <div
                  class="ml-2"
                  :style="{ order: buttonOrder('pacs') }"
                  v-if="isButtonEnabled('pacs')"
                >
                  <PacsButton :has-images="exam.origin.imagem_pacs">
                    <template v-slot:default="{ open }">
                      <v-btn
                        text
                        placeholder="Imagens no PACS"
                        class="link-color text-capitalize font-weight-regular"
                        @click.prevent="open(exam.origin)"
                      >
                        <v-icon left color="primary">mdi-image</v-icon>
                        <span class="link-color">{{ buttonLabel('pacs', 'Imagens (PACS)') }}</span>
                      </v-btn>
                    </template>
                  </PacsButton>
                </div>
              </v-col>
            </v-row>
          </div>
          <v-divider v-if="index < exams.length - 1" />
        </div>
      </div>
    </v-card>
  </div>
</template>
<script>
import Api from '@/resources/NajaSocialApi'
import LoadingContainer from '@/components/LoadingContainer'
import ReportDialog from '@/components/ReportDialog'
import PacsButton from '@/components/PacsButton'
import { orderBy } from 'lodash'
import { mapState } from 'vuex'

export default {
  name: 'ImageExamSections',
  components: { LoadingContainer, ReportDialog, PacsButton },
  data() {
    return {
      loading: false,
      loadingPACS: false,
      exams: [],
    }
  },

  computed: {
    ...mapState({
      showPdfImages: state => state.data.organization.show_pdf_images,
      portalExamsWithoutPACS: state => state.data.organization.portal_exams_without_pacs,
      portalCustomizeActions: state => state.data.organization.portal_customize_actions,
      portalActionsConfig: state => state.data.organization.portal_actions_config,
    }),

    profileActionsConfig() {
      if (!this.portalCustomizeActions || !this.portalActionsConfig) return null
      return this.portalActionsConfig['patient'] || null
    },
  },

  mounted() {
    this.getExams()
  },

  methods: {
    init() {},

    isButtonEnabled(key) {
      if (!this.profileActionsConfig) return true
      const btn = this.profileActionsConfig.find(b => b.key === key)
      return btn ? btn.enabled : true
    },

    buttonOrder(key) {
      if (!this.profileActionsConfig) return 0
      const btn = this.profileActionsConfig.find(b => b.key === key)
      return btn ? parseInt(btn.position) : 0
    },

    buttonLabel(key, defaultLabel) {
      if (!this.profileActionsConfig) return defaultLabel
      const btn = this.profileActionsConfig.find(b => b.key === key)
      return btn && btn.label ? btn.label : defaultLabel
    },

    async getExams() {
      this.loading = true
      try {
        const imageExamsResponse = await Api.imageExams({})
        let exams = orderBy(
          imageExamsResponse.data.exams,
          exam => {
            return this.$moment(exam.date)
          },
          ['desc']
        )

        if (this.portalExamsWithoutPACS === 'dont_show_exams') {
          exams = exams.filter(x => x.origin.imagem_pacs)
        }

        this.exams = exams.slice(0, 6)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.image-exams-section {
  .exams {
    overflow-y: scroll;
    height: 320px;
  }
}
</style>
